import {
  Avatar,
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useLoaderData } from 'react-router-dom';
import { apiAdmin } from '@/api/admin';
import type { UserSummaryDto, UserTenantDto } from '@/api/types';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { useCopy } from '@/hooks/useCopy';

export interface UserDetailLoaderData {
  user: UserSummaryDto;
  tenants: UserTenantDto[];
}

export async function userDetailLoader({
  params,
}: {
  params: { id?: string };
}): Promise<UserDetailLoaderData> {
  const id = Number(params.id);
  const [user, tenants] = await Promise.all([
    apiAdmin.users.get(id),
    apiAdmin.users.tenants(id).catch(() => [] as UserTenantDto[]),
  ]);
  return { user, tenants };
}

export function UserDetail() {
  const { user, tenants } = useLoaderData() as UserDetailLoaderData;
  const { t } = useCopy();
  return (
    <Box>
      <Breadcrumbs
        items={[
          { label: t('nav.home', 'Home'), to: '/' },
          { label: t('nav.users', 'Users'), to: '/users' },
          { label: user.name ?? user.email },
        ]}
      />
      <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: 'center',
          }}
        >
          <Avatar {...(user.imageUrl ? { src: user.imageUrl } : {})} sx={{ width: 56, height: 56 }}>
            {(user.name ?? user.email)[0]?.toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="h1">{user.name ?? user.email}</Typography>
            <Typography
              sx={{
                color: 'text.secondary',
              }}
            >
              {user.email}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              Joined {new Date(user.createdAt).toLocaleDateString()} ·{' '}
              {user.emailVerified ? 'Verified' : 'Unverified'}{' '}
              {user.googleLinked ? '· Google linked' : ''}
            </Typography>
          </Box>
        </Stack>
      </Paper>
      <Typography variant="h2" component="h2" gutterBottom>
        {t('users.detail.memberships', 'Tenant memberships')}
      </Typography>
      {tenants.length === 0 ? (
        <Typography
          sx={{
            color: 'text.secondary',
          }}
        >
          {t('users.detail.noMemberships', '')}
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Tenant</TableCell>
                <TableCell>{t('members.role', 'Role')}</TableCell>
                <TableCell>{t('members.status', 'Status')}</TableCell>
                <TableCell>{t('members.createdAt', 'Added')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tenants.map((tm) => (
                <TableRow key={tm.tenantId}>
                  <TableCell>
                    <Box component={RouterLink} to={`/tenants/${tm.tenantId}`}>
                      {tm.tenantName} ({tm.tenantSlug})
                    </Box>
                  </TableCell>
                  <TableCell>{tm.role}</TableCell>
                  <TableCell>{tm.status}</TableCell>
                  <TableCell>{new Date(tm.createdAt).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
